/**
 * Beta Application Validation Utilities
 *
 * Type-safe validation functions for beta application form fields.
 * Follows established validation patterns from LoginForm and other components.
 */

import type {
  BetaApplicationDraft
} from '../types/BetaApplicationDraft';
import type {
  TradingStyle,
  TradingSessions,
  BetaApplicationStep,
  BetaAccountType,
  PropFirmStatus
} from '../types/BetaApplicationTypes';
import { Brand } from '@enfomentis/shared/config';

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// ===== FIELD VALIDATION FUNCTIONS =====

/**
 * Validates email address format
 * Follows the same pattern as LoginForm validation
 */
export function validateEmail(email: string): ValidationResult {
  if (!email.trim()) {
    return { isValid: false, error: "Email is required" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }

  return { isValid: true };
}

/**
 * Validates full name (non-empty, reasonable length)
 */
export function validateFullName(fullName: string): ValidationResult {
  if (!fullName.trim()) {
    return { isValid: false, error: "Full name is required" };
  }

  if (fullName.trim().length < 2) {
    return { isValid: false, error: "Please enter your full name" };
  }

  if (fullName.length > 100) {
    return { isValid: false, error: "Name is too long" };
  }

  return { isValid: true };
}

/**
 * Validates Discord username format
 */
export function validateDiscordUsername(username: string): ValidationResult {
  if (!username.trim()) {
    return { isValid: false, error: "Discord username is required" };
  }

  // Discord usernames can be 2-32 characters, alphanumeric plus underscore and period
  const discordRegex = /^[a-zA-Z0-9_.]{2,32}$/;
  if (!discordRegex.test(username)) {
    return { isValid: false, error: "Please enter a valid Discord username (2-32 characters, letters, numbers, _ and . only)" };
  }

  return { isValid: true };
}

/**
 * Validates required selection fields
 */
export function validateRequiredSelection(value: string, fieldName: string): ValidationResult {
  if (!value || value === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  return { isValid: true };
}

/**
 * Validates required multi-selection fields
 */
export function validateRequiredMultiSelection(values: string[], fieldName: string): ValidationResult {
  if (!values || values.length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  return { isValid: true };
}

/**
 * Validates trading style other specification (when style is 'other')
 */
export function validateTradingStyleOther(tradingStyle: TradingStyle | '', other?: string): ValidationResult {
  if (tradingStyle === 'other') {
    if (!other || !other.trim()) {
      return { isValid: false, error: "Please specify your trading style" };
    }

    if (other.length > 100) {
      return { isValid: false, error: "Description is too long" };
    }
  }

  return { isValid: true };
}

/**
 * Validates trading sessions other specification (when sessions includes 'other')
 */
export function validateTradingSessionsOther(tradingSessions: TradingSessions[], other?: string): ValidationResult {
  if (tradingSessions.includes('other')) {
    if (!other || !other.trim()) {
      return { isValid: false, error: "Please specify your trading sessions" };
    }

    if (other.length > 100) {
      return { isValid: false, error: "Description is too long" };
    }
  }

  return { isValid: true };
}

/**
 * Validates text fields with reasonable length limits
 */
export function validateTextField(value: string, fieldName: string, maxLength: number = 500): ValidationResult {
  if (!value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (value.length > maxLength) {
    return { isValid: false, error: `${fieldName} is too long (max ${maxLength} characters)` };
  }

  return { isValid: true };
}

/**
 * Validates prop firm details when account type is prop-firm
 */
export function validatePropFirm(
  accountType: BetaAccountType | '',
  propFirm: string,
  propFirmStatus: PropFirmStatus | '',
  propFirmPayoutHistory: string
): ValidationResult {
  // Only validate if account type is prop-firm
  if (accountType !== 'prop-firm') {
    return { isValid: true };
  }

  // Validate prop firm name
  if (!propFirm.trim()) {
    return { isValid: false, error: "Prop firm name is required" };
  }

  if (propFirm.length > 100) {
    return { isValid: false, error: "Prop firm name is too long" };
  }

  // Validate prop firm status
  if (!propFirmStatus) {
    return { isValid: false, error: "Prop firm status is required" };
  }

  // Validate payout history if funded
  if (propFirmStatus === 'funded') {
    if (!propFirmPayoutHistory) {
      return { isValid: false, error: "Payout history is required for funded accounts" };
    }
  }

  return { isValid: true };
}

/**
 * Validates broker name
 */
export function validateBroker(broker: string): ValidationResult {
  if (!broker.trim()) {
    return { isValid: false, error: "Broker name is required" };
  }

  if (broker.length > 100) {
    return { isValid: false, error: "Broker name is too long" };
  }

  return { isValid: true };
}

// ===== STEP VALIDATION FUNCTIONS =====

/**
 * Validates all fields in the Account step
 */
export function validateAccountStep(draft: BetaApplicationDraft): ValidationResult {
  const { account } = draft;

  // Validate email
  const emailResult = validateEmail(account.email);
  if (!emailResult.isValid) return emailResult;

  // Validate full name
  const nameResult = validateFullName(account.fullName);
  if (!nameResult.isValid) return nameResult;

  // Validate Discord username
  const discordResult = validateDiscordUsername(account.discordUsername);
  if (!discordResult.isValid) return discordResult;

  return { isValid: true };
}

/**
 * Validates all fields in the Trading Setup step (Step 2A)
 * Note: This validates only the setup fields - style/experience fields moved to Step 2B
 */
export function validateTradingStep(draft: BetaApplicationDraft): ValidationResult {
  const { trading } = draft;

  // Validate primary focus (multi-select)
  const primaryFocusResult = validateRequiredMultiSelection(trading.primaryFocus, "Primary asset(s) traded");
  if (!primaryFocusResult.isValid) return primaryFocusResult;

  // Validate required Step 2A fields
  const requiredFields: Array<{ value: string; name: string }> = [
    { value: trading.accountType, name: "Account type" },
    { value: trading.broker, name: "Broker" }
  ];

  for (const field of requiredFields) {
    const result = validateRequiredSelection(field.value, field.name);
    if (!result.isValid) return result;
  }

  // Validate prop firm details (conditional)
  const propFirmResult = validatePropFirm(
    trading.accountType,
    trading.propFirm,
    trading.propFirmStatus,
    trading.propFirmPayoutHistory
  );
  if (!propFirmResult.isValid) return propFirmResult;

  // Validate broker name
  const brokerResult = validateBroker(trading.broker);
  if (!brokerResult.isValid) return brokerResult;

  return { isValid: true };
}

/**
 * Validates all fields in the Style & Experience step (Step 3)
 */
export function validateStyleExperienceStep(draft: BetaApplicationDraft): ValidationResult {
  const { trading } = draft;

  // Validate required single selections
  const requiredFields: Array<{ value: string; name: string }> = [
    { value: trading.experienceLevel, name: "Experience level" },
    { value: trading.tradingStyle, name: "Trading style" }
  ];

  for (const field of requiredFields) {
    const result = validateRequiredSelection(field.value, field.name);
    if (!result.isValid) return result;
  }

  // Validate trading sessions (multi-select)
  const tradingSessionsResult = validateRequiredMultiSelection(trading.tradingSessions, "Trading sessions");
  if (!tradingSessionsResult.isValid) return tradingSessionsResult;

  // Validate trading style other specification (conditional)
  const tradingStyleOtherResult = validateTradingStyleOther(trading.tradingStyle, trading.tradingStyleOther);
  if (!tradingStyleOtherResult.isValid) return tradingStyleOtherResult;

  // Validate trading sessions other specification (conditional)
  const tradingSessionsOtherResult = validateTradingSessionsOther(trading.tradingSessions, trading.tradingSessionsOther);
  if (!tradingSessionsOtherResult.isValid) return tradingSessionsOtherResult;

  // Validate current journaling method
  const journalingResult = validateTextField(trading.currentJournaling, "Current journaling method", 500);
  if (!journalingResult.isValid) return journalingResult;

  return { isValid: true };
}

/**
 * Validates all fields in the Commitment step
 */
export function validateCommitmentStep(draft: BetaApplicationDraft): ValidationResult {
  const { commitment } = draft;

  // Validate interest field
  const stjInterestResult = validateTextField(commitment.stjInterest, `Interest in ${Brand.displayName} beta testing`, 500);
  if (!stjInterestResult.isValid) return stjInterestResult;

  // Validate required selections
  const testingResult = validateRequiredSelection(commitment.testingCommitment, "Testing commitment");
  if (!testingResult.isValid) return testingResult;

  const feedbackResult = validateRequiredSelection(commitment.feedbackCommitment, "Feedback commitment");
  if (!feedbackResult.isValid) return feedbackResult;

  return { isValid: true };
}

/**
 * Validates a specific step of the application
 */
export function validateStep(step: BetaApplicationStep, draft: BetaApplicationDraft): ValidationResult {
  switch (step) {
    case 'account':
      return validateAccountStep(draft);
    case 'trading':
      return validateTradingStep(draft);
    case 'style-experience':
      return validateStyleExperienceStep(draft);
    case 'commitment':
      return validateCommitmentStep(draft);
    case 'review':
      return { isValid: true }; // Review step is always valid (read-only)
    default:
      return { isValid: false, error: "Unknown step" };
  }
}

/**
 * Validates the entire application before submission
 */
export function validateCompleteApplication(draft: BetaApplicationDraft): ValidationResult {
  // Validate each step (excluding review as it's read-only)
  const steps: BetaApplicationStep[] = ['account', 'trading', 'style-experience', 'commitment'];

  for (const step of steps) {
    const result = validateStep(step, draft);
    if (!result.isValid) {
      return { isValid: false, error: `${step} step: ${result.error}` };
    }
  }

  return { isValid: true };
}

/**
 * Checks if a step can be marked as completed
 */
export function canCompleteStep(step: BetaApplicationStep, draft: BetaApplicationDraft): boolean {
  const result = validateStep(step, draft);
  return result.isValid;
}